import styled from "styled-components";
import { useState, useEffect } from "react";
import { Col, Grid, Modal, Row, Skeleton } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import {
	FormRequestFlightAdditionalInfo,
	FormRequestFlightInfo,
	FormRequestFlightSelectCategory,
	FormRequestFlightSummary,
} from "@/components/organisms";
import { DATETIME_FORMAT_STANDARD, NOTIFICATION_TYPE } from "@/config/constants";
import { initFormRequest } from "@/utils/initializing";
import { createCharterBooking } from "@/stores/apis/apiCharter";
import { readTrafficSource } from "@/utils/trafficSource";
import { toast } from "@/modules/toast";
import { ModalStatus } from "../ModalStatus";
import { RATypography } from "@/components/customs";
import { noop } from "lodash";
import dayjs from "dayjs";

const FlightRequestStep = {
	SELECT_LEG_TYPE: 0,
	SELECT_AIRCRAFT_CATEGORY: 1,
	SELECT_ADDITIONAL_INFO: 2,
	SUMMARY: 3,
};
const { useBreakpoint } = Grid;

export function ModalRerequestCharter({ visible = false, onClose = noop, booking, onRerequestSuccess = noop }) {
	const screens = useBreakpoint();
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(FlightRequestStep.SELECT_LEG_TYPE);
	const [formRequest, setFormRequest] = useState(initFormRequest);
	const [showStatus, setShowStatus] = useState(false);

	useEffect(() => {
		if (booking && booking._id) {
			setFormRequest({ ...booking });
		}
	}, [booking]);

	const onNext = () => {
		setStep(step + 1);
	};

	const onBack = () => {
		setStep(step - 1);
	};

	const onSubmitRequest = async () => {
		let payload = {
			type: formRequest.type,
			legs:
				formRequest.legs?.map((v) => {
					// format time range with timezone
					let timezone = v?.from_airport?.location?.timezone;
					let departure_datetime = v?.departure_datetime;
					if (departure_datetime && timezone) {
						let value = dayjs(departure_datetime).format(DATETIME_FORMAT_STANDARD);
						departure_datetime = dayjs.tz(value, timezone).toISOString();
					}

					let departure_window_datetime = v?.departure_window_datetime;
					if (departure_window_datetime && timezone) {
						let value = dayjs(departure_window_datetime).format(DATETIME_FORMAT_STANDARD);
						departure_window_datetime = dayjs.tz(value, timezone).toISOString();
					}

					return {
						from_airport: v?.from_airport?._id,
						to_airport: v?.to_airport?._id,
						departure_datetime: departure_datetime,
						departure_window_datetime: departure_window_datetime,
						passengers: v?.passengers ?? 1,
					};
				}) ?? [],
			aircraft_categories: formRequest.aircraft_categories?.map((v) => v._id) ?? [],
			note: formRequest.note,
			additional_info: {
				is_bring_pet: formRequest.additional_info.is_bring_pet,
				is_bring_oversize_luggage: formRequest.additional_info.is_bring_oversize_luggage,
				is_interest_empty_leg: formRequest.additional_info.is_interest_empty_leg,
			},
		};

		if (loading) return;
		setLoading(true);

		const traffic_source = readTrafficSource();

		let result = await createCharterBooking({
			...payload,
			traffic_source,
		});
		if (result && result.success) {
			setShowStatus(true);
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Flight Request Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const _renderConentByType = () => {
		switch (step) {
			case FlightRequestStep.SELECT_LEG_TYPE:
				return (
					<FormRequestFlightInfo
						data={formRequest}
						onChange={(values) => setFormRequest({ ...values })}
						allowSelectAirport={false}
						onNext={onNext}
						switchOptions={false}
						headerRender={
							<Row>
								<Col md={24}>
									<WrapperContent>
										<RATypography variant="h3">Re-request A Flight</RATypography>
									</WrapperContent>
									<RATypography variant="body3" top="12px" color="#595959">
										Enter your flight details below and our team will contact you shortly. Private
										jet charter flight prices are subject to the market rate
									</RATypography>
								</Col>
							</Row>
						}
					/>
				);
			case FlightRequestStep.SELECT_AIRCRAFT_CATEGORY:
				return (
					<FormRequestFlightSelectCategory
						data={formRequest}
						onChange={(values) => setFormRequest({ ...values })}
						onNext={onNext}
						onBack={onBack}
					/>
				);
			case FlightRequestStep.SELECT_ADDITIONAL_INFO:
				return (
					<FormRequestFlightAdditionalInfo
						data={formRequest}
						onChange={(values) => setFormRequest({ ...values })}
						onNext={onNext}
						onBack={onBack}
					/>
				);
			case FlightRequestStep.SUMMARY:
				return (
					<FormRequestFlightSummary
						data={formRequest}
						onNext={onNext}
						onBack={onBack}
						onSubmitRequest={onSubmitRequest}
						loading={loading}
						formatTimezone={false}
					/>
				);
			default:
				return <Skeleton style={{ margin: "0 12px" }} />;
		}
	};

	const getWidthModal = () => {
		let width = "auto";
		if (screens.md || screens.lg || screens.xl || screens.xxl) {
			width = "1000px";
		}
		return width;
	};

	return (
		<Modal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => {
				setStep(FlightRequestStep.SELECT_LEG_TYPE);
				setFormRequest(initFormRequest);
				onClose(false);
			}}
			footer={null}
			maskClosable={false}
			wrapClassName="auth-wrapper"
			width={getWidthModal()}
			destroyOnClose
		>
			<Container>{_renderConentByType()}</Container>
			<ModalStatus
				visible={showStatus}
				status={NOTIFICATION_TYPE.SUCCESS}
				title="Flight Request Sent Successfully!"
				note="Thank you for your request! Our team will be in contact with you shortly."
				onClose={() => {
					setShowStatus(false);
					setTimeout(() => {
						setStep(FlightRequestStep.SELECT_LEG_TYPE);
						setFormRequest(initFormRequest);
						onRerequestSuccess();
						onClose();
					}, 300);
				}}
			/>
		</Modal>
	);
}

const Container = styled.div`
	padding-top: 30px;
	padding-bottom: 30px;
	max-width: 1000px;
	min-width: 343px;
	padding: clamp(24px, 8vw, 60px) clamp(16px, 4vw, 60px);
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
